import { t } from "@lingui/core/macro"
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query"
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router"
import {
  Archive,
  ArrowLeft,
  Check,
  Copy,
  FileText,
  History,
  RotateCcw,
  Settings2,
  Trash2,
  Upload,
} from "lucide-react"
import { useState, useRef, useCallback } from "react"
import { toast } from "sonner"
import { z } from "zod"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { CloneVersionDialog } from "@/features/store-configuration/components/CloneVersionDialog"
import { DeleteVersionDialog } from "@/features/store-configuration/components/DeleteVersionDialog"
import { ImportVersionDialog } from "@/features/store-configuration/components/ImportVersionDialog"
import { useConfigVersionMutations } from "@/features/store-configuration/hooks/useConfigVersionMutations"
import {
  listConfigurationVersionsFn,
  type ConfigurationVersionSummary,
} from "@/features/store-configuration/server/storeConfiguration"
import {
  analyzeImportFile,
  type ImportPreview,
  type ValidationError,
} from "@/features/store-configuration/utils/importPreview"
import { usePermissions } from "@/hooks/usePermissions"
import { storeConfigurationQueryKeys } from "@/lib/queryKeys"
import { cn } from "@/lib/utils"

const listVersionsSearchParams = z.object({
  includeArchived: z.boolean().optional().default(false),
})

const configurationVersionsQuery = (filters: z.infer<typeof listVersionsSearchParams>) =>
  queryOptions({
    queryKey: storeConfigurationQueryKeys.list(filters),
    queryFn: () => listConfigurationVersionsFn({ data: filters }),
  })

export const Route = createFileRoute("/(authenticated)/settings/configuration-versions/")({
  validateSearch: listVersionsSearchParams,
  loaderDeps: ({ search }) => ({
    includeArchived: search.includeArchived,
  }),
  loader: ({ context, deps }) => {
    void context.queryClient.ensureQueryData(configurationVersionsQuery(deps))
  },
  component: StoreConfigurationListPage,
})

function StoreConfigurationListPage() {
  const filters = Route.useSearch()
  const { data: versions } = useSuspenseQuery(configurationVersionsQuery(filters))
  const navigate = useNavigate({ from: Route.fullPath })
  const { hasPermission } = usePermissions()

  // Permission checks
  const canCreate = hasPermission("configVersion", "create").allowed
  const canUpdate = hasPermission("configVersion", "update").allowed
  const canDelete = hasPermission("configVersion", "delete").allowed
  const canViewAuditLog = hasPermission("auditLog", "read").allowed

  const [cloneDialogOpen, setCloneDialogOpen] = useState(false)
  const [versionToClone, setVersionToClone] = useState<ConfigurationVersionSummary | null>(null)
  const [cloneName, setCloneName] = useState("")
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [versionToDelete, setVersionToDelete] = useState<ConfigurationVersionSummary | null>(null)
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [importVersionLabel, setImportVersionLabel] = useState("")
  const [importFile, setImportFile] = useState<File | null>(null)
  const [importJsonText, setImportJsonText] = useState<string | null>(null)
  const [importPreview, setImportPreview] = useState<ImportPreview | null>(null)
  const [importParseError, setImportParseError] = useState<string | null>(null)
  const [importValidationErrors, setImportValidationErrors] = useState<ValidationError[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  const {
    cloneMutation,
    activateMutation,
    archiveMutation,
    unarchiveMutation,
    deleteMutation,
    importMutation,
  } = useConfigVersionMutations({
    onCloneSuccess: () => {
      setCloneDialogOpen(false)
      setVersionToClone(null)
      setCloneName("")
    },
    onDeleteSuccess: () => {
      setDeleteDialogOpen(false)
      setVersionToDelete(null)
    },
    onImportSuccess: () => {
      setImportDialogOpen(false)
      setImportVersionLabel("")
      setImportFile(null)
      setImportJsonText(null)
      setImportPreview(null)
      setImportParseError(null)
      setImportValidationErrors([])
    },
  })

  const MAX_IMPORT_SIZE = 5 * 1024 * 1024 // 5MB

  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Reset input so same file can be selected again
    event.target.value = ""

    if (file.size > MAX_IMPORT_SIZE) {
      toast.error(t`File is too large. Maximum size is 5MB.`)
      return
    }

    setImportFile(file)
    const baseName = file.name.replace(/\.json$/i, "")
    setImportVersionLabel(baseName)

    // Read file and analyze for preview
    try {
      const text = await file.text()
      setImportJsonText(text)

      const analysis = analyzeImportFile(text)
      if (analysis.ok) {
        setImportPreview(analysis.preview)
        setImportParseError(null)
        setImportValidationErrors([])
      } else {
        setImportPreview(null)
        setImportParseError(analysis.error)
        setImportValidationErrors(analysis.validationErrors ?? [])
      }
    } catch {
      setImportJsonText(null)
      setImportPreview(null)
      setImportParseError(t`Failed to read file`)
    }

    setImportDialogOpen(true)
  }, [])

  const handleImportClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleConfirmImport = useCallback(() => {
    if (!importJsonText || !importVersionLabel.trim()) return

    importMutation.mutate({
      data: {
        json: importJsonText,
        versionLabel: importVersionLabel.trim(),
      },
    })
  }, [importJsonText, importVersionLabel, importMutation])

  const handleClone = (version: ConfigurationVersionSummary) => {
    setVersionToClone(version)
    setCloneName(`${version.versionLabel} (copy)`)
    setCloneDialogOpen(true)
  }

  const handleConfirmClone = () => {
    if (!versionToClone || !cloneName.trim()) return
    cloneMutation.mutate({
      data: {
        sourceVersionId: versionToClone.id,
        newVersionLabel: cloneName.trim(),
      },
    })
  }

  const handleActivate = (version: ConfigurationVersionSummary) => {
    if (version.isActive) return
    activateMutation.mutate({ data: { id: version.id } })
  }

  const handleArchive = (version: ConfigurationVersionSummary) => {
    if (version.isActive) {
      toast.error(t`Cannot archive the active configuration`)
      return
    }
    archiveMutation.mutate({ data: { id: version.id } })
  }

  const handleUnarchive = (version: ConfigurationVersionSummary) => {
    unarchiveMutation.mutate({ data: { id: version.id } })
  }

  const handleDelete = (version: ConfigurationVersionSummary) => {
    if (!version.archivedAt) {
      toast.error(t`Can only delete archived configurations`)
      return
    }
    setVersionToDelete(version)
    setDeleteDialogOpen(true)
  }

  const handleConfirmDelete = () => {
    if (!versionToDelete) return
    deleteMutation.mutate({ data: { id: versionToDelete.id } })
  }

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  return (
    <>
      <div className="flex-1 space-y-6 overflow-y-auto pb-8">
        <div className="flex items-start justify-between gap-6">
          <div>
            <div className="mb-2 text-xs uppercase tracking-wider text-neutral-500 dark:text-neutral-300">
              {t`Configuration`}
            </div>
            <h1 className="mb-2 font-serif text-4xl font-bold text-neutral-900 dark:text-white">
              {t`Configuration Versions`}
            </h1>
            <p className="text-neutral-500 dark:text-neutral-300">
              {t`Manage versioned configuration including bandwidths, rules, and categories`}
            </p>
          </div>
        </div>

        {/* Hidden file input for import */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept=".json,application/json"
          className="hidden"
        />

        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <Button
              variant={filters.includeArchived ? "secondary" : "ghost"}
              size="sm"
              onClick={() => {
                void navigate({
                  search: (prev: z.infer<typeof listVersionsSearchParams>) => ({
                    ...prev,
                    includeArchived: !prev.includeArchived,
                  }),
                  replace: true,
                })
              }}
            >
              <Archive className="mr-2 size-4" />
              {filters.includeArchived ? t`Hide Archived` : t`Show Archived`}
            </Button>
            {canCreate && (
              <Button variant="outline" size="sm" onClick={handleImportClick}>
                <Upload className="mr-2 size-4" />
                {t`Import JSON`}
              </Button>
            )}
          </div>

          <div className="border-neutral-150 overflow-hidden rounded-lg border bg-white shadow-sm dark:border-neutral-700 dark:bg-neutral-900/50">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="px-4 text-left font-medium">{t`Version`}</TableHead>
                    <TableHead className="px-4 text-left font-medium">{t`Status`}</TableHead>
                    <TableHead className="px-4 text-left font-medium">{t`Last Updated`}</TableHead>
                    <TableHead className="px-4 text-left font-medium">{t`Created`}</TableHead>
                    <TableHead className="px-4 text-right font-medium">{t`Actions`}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {versions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="h-24 px-4 text-center">
                        <FileText className="mx-auto mb-2 h-5 w-5 text-gray-400" />
                        <p>{t`No configuration versions found.`}</p>
                        <p className="mt-1 text-sm text-neutral-400">
                          {t`Run the seed script to import the default configuration.`}
                        </p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    versions.map((version) => (
                      <TableRow
                        key={version.id}
                        className={cn(
                          "cursor-pointer transition-colors hover:bg-neutral-50 dark:hover:bg-neutral-800/50",
                          version.archivedAt && "opacity-60",
                        )}
                        onClick={() => {
                          void navigate({
                            to: "/settings/configuration-versions/$versionId",
                            params: { versionId: version.id },
                          })
                        }}
                      >
                        <TableCell className="px-4 font-medium">
                          <div className="flex items-center gap-2">
                            <Settings2 className="size-4 text-neutral-400" />
                            {version.versionLabel}
                          </div>
                        </TableCell>
                        <TableCell className="px-4">
                          <div className="flex gap-2">
                            {version.isActive && (
                              <Badge variant="success">
                                <Check className="mr-1 size-3" />
                                {t`Active`}
                              </Badge>
                            )}
                            {version.archivedAt && (
                              <Badge variant="secondary">
                                <Archive className="mr-1 size-3" />
                                {t`Archived`}
                              </Badge>
                            )}
                            {!version.isActive && !version.archivedAt && (
                              <Badge variant="outline">{t`Draft`}</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="px-4 text-neutral-500">
                          {formatDate(version.lastUpdated)}
                        </TableCell>
                        <TableCell className="px-4 text-neutral-500">
                          {formatDate(version.createdAt)}
                          {version.createdBy && (
                            <span className="ml-1 text-neutral-400">by {version.createdBy}</span>
                          )}
                        </TableCell>
                        <TableCell className="px-4 text-right">
                          {/* oxlint-disable-next-line jsx-a11y/click-events-have-key-events -- Event propagation stopper, not interactive */}
                          <div
                            className="flex items-center justify-end gap-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {canCreate && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleClone(version)}
                                title={t`Clone`}
                              >
                                <Copy className="size-4" />
                              </Button>
                            )}
                            {canUpdate && !version.isActive && !version.archivedAt && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleActivate(version)}
                                title={t`Activate`}
                              >
                                <Check className="size-4" />
                              </Button>
                            )}
                            {canUpdate && !version.isActive && !version.archivedAt && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleArchive(version)}
                                title={t`Archive`}
                              >
                                <Archive className="size-4" />
                              </Button>
                            )}
                            {version.archivedAt && (
                              <>
                                {canUpdate && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleUnarchive(version)}
                                    title={t`Restore`}
                                  >
                                    <RotateCcw className="size-4" />
                                  </Button>
                                )}
                                {canDelete && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-500 hover:text-red-600"
                                    onClick={() => handleDelete(version)}
                                    title={t`Delete`}
                                  >
                                    <Trash2 className="size-4" />
                                  </Button>
                                )}
                              </>
                            )}
                            {canViewAuditLog && (
                              <Link
                                to="/settings/audit"
                                search={{
                                  entityType: "store-configuration",
                                  entityId: version.id,
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Button variant="ghost" size="sm" title={t`View Audit Log`}>
                                  <History className="size-4" />
                                </Button>
                              </Link>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8">
        <Link
          to="/settings"
          className="inline-flex items-center text-base text-blue-600 hover:text-blue-800 hover:underline"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t`Back to Configuration Dashboard`}
        </Link>
      </div>

      <CloneVersionDialog
        open={cloneDialogOpen}
        onOpenChange={setCloneDialogOpen}
        versionLabel={versionToClone?.versionLabel ?? null}
        cloneName={cloneName}
        onCloneNameChange={setCloneName}
        onConfirm={handleConfirmClone}
        onCancel={() => {
          setCloneDialogOpen(false)
          setVersionToClone(null)
          setCloneName("")
        }}
        isPending={cloneMutation.isPending}
      />

      <DeleteVersionDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        versionLabel={versionToDelete?.versionLabel ?? null}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setDeleteDialogOpen(false)
          setVersionToDelete(null)
        }}
        isPending={deleteMutation.isPending}
      />

      <ImportVersionDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        fileName={importFile?.name ?? null}
        versionLabel={importVersionLabel}
        onVersionLabelChange={setImportVersionLabel}
        onConfirm={handleConfirmImport}
        onCancel={() => {
          setImportDialogOpen(false)
          setImportFile(null)
          setImportVersionLabel("")
          setImportJsonText(null)
          setImportPreview(null)
          setImportParseError(null)
          setImportValidationErrors([])
        }}
        isPending={importMutation.isPending}
        preview={importPreview}
        parseError={importParseError}
        validationErrors={importValidationErrors}
      />
    </>
  )
}
